import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { upgradeAnbieten } from '../../billtoro/upgrade';
import { Dialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { fehlerText, TarifMarke } from '../tarifHinweis';
import { JahrWahl, type SeitenProps } from './Steuer';
import { basis, centsAus, deDatum, euro, euroGanz, herunterladen, type Abgabe, type ElsterAuftrag, type UstvaDetail, type UstvaListe } from './daten';

const ART_TEXT: Record<Abgabe['art'], string> = { elster: 'über ELSTER gesendet', test: 'Test', hand: 'selbst in ELSTER eingetragen' };

// Umsatzsteuer-Voranmeldung (/rechnung/steuer/ustva): je Monat oder Quartal die fertigen Zahlen, die Frist und ob schon
// abgegeben. Ist die Übermittlung an ELSTER noch nicht eingeschaltet, trägt die Firma die Zahlen in „Mein ELSTER“ ein
// und vermerkt die Abgabe hier.
export function Ustva({ data }: SeitenProps) {
  const [jahr, setJahr] = useState(() => Number(data.today.slice(0, 4)));
  const [l, setL] = useState<UstvaListe | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    setL(null);
    api
      .get<UstvaListe>(`${basis(data.firm.id)}/ustva?jahr=${jahr}`)
      .then((r) => !weg && (setL(r), setError(null)))
      .catch((err) => !weg && setError(errorText(err)));
    return () => {
      weg = true;
    };
  }, [data.firm.id, jahr]);

  if (data.absender.kleinunternehmer) {
    return (
      <>
        <div className="rt-head">
          <h1>Umsatzsteuer</h1>
        </div>
        <div className="rt-card rt-empty">
          <p>Als Kleinunternehmer geben Sie keine Umsatzsteuer-Voranmeldung ab. Hier ist nichts zu tun.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>Umsatzsteuer</h1>
          <p className="muted">
            {l ? `${l.einstellung.zeitraum === 'monat' ? 'Monatlich' : 'Vierteljährlich'}, ${l.einstellung.versteuerung === 'ist' ? 'nach Zahlungseingang' : 'nach Rechnungsdatum'}. ` : ''}
            <Link to="/rechnung/steuer/einstellungen">Ändern</Link>
          </p>
        </div>
        <div className="rt-head-act">
          <JahrWahl jahr={jahr} heute={data.today} onChange={setJahr} />
        </div>
      </div>
      {error && <div className="notice bad">{error}</div>}
      {l && !l.einstellung.gespeichert && (
        <div className="notice warn">
          Bitte einmal in den <Link to="/rechnung/steuer/einstellungen">Einstellungen</Link> prüfen, ob Sie monatlich oder vierteljährlich abgeben.
        </div>
      )}
      {!l ? (
        !error && <div className="spinner" aria-label="Lädt" />
      ) : l.zeitraeume.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Für {jahr} gibt es hier nichts abzugeben.</p>
        </div>
      ) : (
        <ul className="ag-liste">
          {l.zeitraeume.map((z) => (
            <li key={z.zeitraum} className="ag-zeile ag-ustva">
              <Link to={`/rechnung/steuer/ustva/${jahr}/${z.zeitraum}`} className="ag-was ag-link">
                <b>{z.name}</b>
                <span className="muted">{z.laeuft ? 'läuft noch' : `abgeben bis ${deDatum(z.frist)}`}</span>
              </Link>
              <span className="ag-status">
                {z.abgegeben ? (
                  z.geaendert ? (
                    <span className="badge b-warn">geändert, neu abgeben</span>
                  ) : (
                    <span className="badge b-ok">abgegeben {deDatum(z.abgegeben.am)}</span>
                  )
                ) : z.laeuft ? (
                  <span className="badge b-none">läuft</span>
                ) : z.frist < l.heute ? (
                  <span className="badge b-bad">überfällig</span>
                ) : (
                  <span className="badge b-warn">offen</span>
                )}
              </span>
              <span className="ag-betrag num">
                {z.zahllast >= 0 ? euro(z.zahllast) : `${euro(-z.zahllast)} zurück`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="ag-fuss muted">Zahlen aus Ihren Rechnungen, Ausgaben und dem Kassenbuch. Ein Klick zeigt, was in welches Feld kommt.</p>
    </>
  );
}

const ORDNUNG = ['Kz81', 'Kz86', 'Kz41', 'Kz21', 'Kz45', 'Kz48', 'Kz60', 'Kz89', 'Kz93', 'Kz46', 'Kz47', 'Kz84', 'Kz85', 'Kz66', 'Kz61', 'Kz67', 'Kz39', 'Kz83'];

export function UstvaZeitraum({ data, fehltUstva }: SeitenProps) {
  const toast = useToast();
  const p = useParams();
  const jahr = Number(p.jahr);
  const zeitraum = Number(p.zeitraum);
  const [u, setU] = useState<UstvaDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'vermerken' | 'senden' | null>(null);
  const url = `${basis(data.firm.id)}/ustva/${jahr}/${zeitraum}`;

  const laden = useCallback(async () => {
    try {
      setU(await api.get<UstvaDetail>(url));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [url]);
  useEffect(() => {
    void laden();
  }, [laden]);

  function oeffne(d: 'vermerken' | 'senden') {
    if (fehltUstva) upgradeAnbieten(data.tarif, 'ustva');
    else setDialog(d);
  }

  const wert = (kz: string, v: number) => (u?.kzEuro.includes(kz) ? euroGanz(v) : euro(v));
  const letzte = u?.abgaben.find((a) => a.art !== 'test');

  return (
    <>
      <p className="ag-zurueck">
        <Link to="/rechnung/steuer/ustva">← Alle Zeiträume</Link>
      </p>
      {error && <div className="notice bad">{error}</div>}
      {!u ? (
        !error && <div className="spinner" aria-label="Lädt" />
      ) : (
        <>
          <div className="rt-head">
            <div>
              <h1>Umsatzsteuer {u.periode.name}</h1>
              <p className="muted">
                Abgeben bis {deDatum(u.frist)} · aus {u.anzahl.umsaetze} {u.anzahl.umsaetze === 1 ? 'Einnahme' : 'Einnahmen'} und {u.anzahl.ausgaben}{' '}
                {u.anzahl.ausgaben === 1 ? 'Ausgabe' : 'Ausgaben'}
              </p>
            </div>
          </div>

          <div className="ag-zahllast rt-card">
            <span>{u.zahllastCents >= 0 ? 'Sie zahlen ans Finanzamt' : 'Sie bekommen zurück'}</span>
            <b className="num">{euro(Math.abs(u.zahllastCents))}</b>
            <small className="muted">
              Umsatzsteuer {euro(u.umsatzsteuerCents)} − Vorsteuer {euro(u.vorsteuerCents)}
            </small>
            {letzte && (
              <span className={`badge ${u.geaendert ? 'b-warn' : 'b-ok'}`}>
                {u.geaendert ? 'Seit der Abgabe geändert: bitte neu abgeben (Berichtigung)' : `Abgegeben am ${deDatum(letzte.gesendetAm)}`}
              </span>
            )}
          </div>

          {u.hinweise.map((h) => (
            <div key={h} className="notice warn">
              {h}
            </div>
          ))}

          <div className="rt-card">
            <h2 className="ag-h2">So tragen Sie es in ELSTER ein</h2>
            <table className="ag-tabelle ag-kz">
              <tbody>
                {ORDNUNG.filter((kz) => u.kennzahlen[kz] !== undefined && (u.kennzahlen[kz] !== 0 || kz === 'Kz83')).map((kz) => (
                  <tr key={kz} className={kz === 'Kz83' ? 'ag-kz-summe' : undefined}>
                    <td className="num ag-kz-nr">{kz.replace('Kz', '')}</td>
                    <td>{u.kzText[kz] ?? kz}</td>
                    <td className="r num">{wert(kz, u.kennzahlen[kz]!)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {Object.keys(u.kennzahlen).length <= 1 && <p className="muted">Keine Umsätze und keine Vorsteuer: Sie geben eine Voranmeldung mit 0 € ab.</p>}
            {u.steuernummer ? (
              <p className="muted ag-klein">Steuernummer {u.steuernummer}</p>
            ) : (
              <div className="notice warn">
                Ihre Steuernummer fehlt noch in <Link to="/rechnung/angaben">Meine Angaben</Link>.
              </div>
            )}
          </div>

          {u.inhaber && (
            <div className="ag-aktionen">
              {u.elster.bereit ? (
                <button type="button" className={`btn primary${fehltUstva ? ' rt-gesperrt' : ''}`} disabled={fehltUstva === 'bald'} onClick={() => oeffne('senden')}>
                  An ELSTER senden
                  <TarifMarke tarif={data.tarif} fehlt={fehltUstva} />
                </button>
              ) : (
                <div className="notice info ag-elster">
                  Direkt aus Billtoro senden kommt bald. Bis dahin: In{' '}
                  <a href="https://www.elster.de/eportal/start" target="_blank" rel="noreferrer">
                    Mein ELSTER
                  </a>{' '}
                  „Umsatzsteuer-Voranmeldung“ öffnen, die Zahlen oben eintragen, senden und hier vermerken.
                </div>
              )}
              <button type="button" className={`btn${u.elster.bereit ? '' : ' primary'}${fehltUstva ? ' rt-gesperrt' : ''}`} disabled={fehltUstva === 'bald'} onClick={() => oeffne('vermerken')}>
                Als abgegeben vermerken
                {!u.elster.bereit && <TarifMarke tarif={data.tarif} fehlt={fehltUstva} />}
              </button>
            </div>
          )}

          {u.abgaben.length > 0 && (
            <div className="rt-card">
              <h2 className="ag-h2">Abgaben</h2>
              <ul className="ag-abgaben">
                {u.abgaben.map((a) => (
                  <li key={a.id}>
                    <span>
                      {deDatum(a.gesendetAm)} · {ART_TEXT[a.art]}
                      {a.berichtigt ? ' · Berichtigung' : ''}
                      {a.ticket ? ` · Ticket ${a.ticket}` : ''}
                    </span>
                    <span className="num">{euro(a.zahllast)}</span>
                    <button
                      type="button"
                      className="linkbtn"
                      onClick={() =>
                        void herunterladen(`${basis(data.firm.id)}/ustva/abgabe/${a.id}/protokoll`, `UStVA ${u.periode.name}.pdf`).catch((e) => toast(errorText(e)))
                      }
                    >
                      {a.protokoll ? 'Protokoll' : 'Aufstellung'}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dialog === 'vermerken' && (
            <VermerkenDialog
              url={url}
              heute={data.today}
              sonder={u.sondervorauszahlung}
              onClose={() => setDialog(null)}
              onSaved={async () => {
                setDialog(null);
                toast('Abgabe vermerkt');
                await laden();
              }}
            />
          )}
          {dialog === 'senden' && (
            <SendenDialog
              url={url}
              name={u.periode.name}
              zahllast={u.zahllastCents}
              sonder={u.sondervorauszahlung}
              auftrag={u.auftrag}
              auftragUrl={`${basis(data.firm.id)}/elster-auftrag`}
              onClose={() => setDialog(null)}
              onSaved={async (test) => {
                setDialog(null);
                toast(test ? 'An das Testsystem von ELSTER gesendet' : 'An ELSTER gesendet');
                await laden();
              }}
            />
          )}
        </>
      )}
    </>
  );
}

function SonderFeld({ wert, onChange }: { wert: string; onChange(v: string): void }) {
  return (
    <label className="field">
      <span>Sondervorauszahlung abziehen (optional)</span>
      <input value={wert} inputMode="decimal" placeholder="0,00" onChange={(e) => onChange(e.target.value)} />
      <small>Nur wenn Sie für dieses Jahr eine Sondervorauszahlung geleistet haben (Dezember bzw. 4. Quartal).</small>
    </label>
  );
}

function VermerkenDialog({ url, heute, sonder, onClose, onSaved }: { url: string; heute: string; sonder: boolean; onClose(): void; onSaved(): Promise<void> }) {
  const [am, setAm] = useState(heute);
  const [sv, setSv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function los(e: FormEvent) {
    e.preventDefault();
    const c = centsAus(sv);
    if (c === undefined || (c !== null && c < 0)) return setError('Bitte die Sondervorauszahlung prüfen.');
    setBusy(true);
    try {
      await api.post(`${url}/vermerken`, { gesendetAm: am, sondervorauszahlungCents: c });
      await onSaved();
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open onClose={onClose} title="Als abgegeben vermerken" className="ag-dlg-klein">
      <form onSubmit={(e) => void los(e)} noValidate>
        <p className="muted">Billtoro merkt sich die Zahlen von jetzt. Ändert sich danach etwas, sehen Sie es hier.</p>
        <label className="field">
          <span>In ELSTER gesendet am</span>
          <input type="date" value={am} max={heute} onChange={(e) => setAm(e.target.value)} />
        </label>
        {sonder && <SonderFeld wert={sv} onChange={setSv} />}
        {error && <div className="notice bad">{error}</div>}
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            Vermerken
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// Senden auf zwei Wegen: „Billtoro sendet für Sie“ (wie bei Lexware, ohne eigenes Zertifikat; beim ersten Mal stimmt
// die Firma einmal zu) oder mit dem eigenen ELSTER-Zertifikat (.pfx) und PIN, die nicht gespeichert werden
function SendenDialog({
  url,
  name,
  zahllast,
  sonder,
  auftrag,
  auftragUrl,
  onClose,
  onSaved,
}: {
  url: string;
  name: string;
  zahllast: number;
  sonder: boolean;
  auftrag: ElsterAuftrag;
  auftragUrl: string;
  onClose(): void;
  onSaved(test: boolean): Promise<void>;
}) {
  const [weg, setWeg] = useState<'auftrag' | 'eigen'>(auftrag.moeglich ? 'auftrag' : 'eigen');
  const [zustimmung, setZustimmung] = useState(false);
  const [geprueft, setGeprueft] = useState(false);
  const datei = useRef<HTMLInputElement>(null);
  const [zert, setZert] = useState<File | null>(null);
  const [pin, setPin] = useState('');
  const [sv, setSv] = useState('');
  const [busy, setBusy] = useState<'pruefen' | 'senden' | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function los(nurPruefen: boolean) {
    setError(null);
    setOk(null);
    const c = centsAus(sv);
    if (c === undefined || (c !== null && c < 0)) return setError('Bitte die Sondervorauszahlung prüfen.');
    const imAuftrag = weg === 'auftrag';
    if (imAuftrag && !auftrag.erteilt && !zustimmung) return setError('Bitte zustimmen, dass Billtoro für Sie sendet.');
    if (imAuftrag && !nurPruefen && !geprueft) return setError('Bitte bestätigen, dass Sie die Zahlen geprüft haben.');
    if (!imAuftrag && !nurPruefen && (!zert || !pin)) return setError('Bitte Ihre Zertifikatsdatei und die PIN angeben.');
    const fd = new FormData();
    if (nurPruefen) fd.append('nurPruefen', '1');
    if (c) fd.append('sondervorauszahlungCents', String(c));
    if (imAuftrag) fd.append('modus', 'auftrag');
    else {
      fd.append('pin', pin);
      if (zert) fd.append('zertifikat', zert, zert.name);
    }
    setBusy(nurPruefen ? 'pruefen' : 'senden');
    try {
      if (imAuftrag && !auftrag.erteilt) await api.post(auftragUrl, { zustimmung: true });
      const r = await api.post<{ geprueft?: boolean; test?: boolean }>(`${url}/senden`, fd);
      if (r.geprueft) setOk('ELSTER hat die Voranmeldung geprüft: alles in Ordnung.');
      else await onSaved(!!r.test);
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open onClose={onClose} title={`Voranmeldung ${name} senden`} className="ag-dlg-klein">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void los(false);
        }}
        noValidate
      >
        <p>
          {zahllast >= 0 ? 'Zahllast' : 'Erstattung'}: <b className="num">{euro(Math.abs(zahllast))}</b>
        </p>
        {auftrag.moeglich && (
          <div className="ag-sendewege" role="radiogroup" aria-label="Wie senden?">
            <label className={`ag-sendeweg${weg === 'auftrag' ? ' an' : ''}`}>
              <input type="radio" name="weg" checked={weg === 'auftrag'} onChange={() => setWeg('auftrag')} />
              <span>
                <b>Billtoro sendet für Sie</b>
                <small className="muted">Empfohlen. Ohne eigenes ELSTER-Zertifikat.</small>
              </span>
            </label>
            <label className={`ag-sendeweg${weg === 'eigen' ? ' an' : ''}`}>
              <input type="radio" name="weg" checked={weg === 'eigen'} onChange={() => setWeg('eigen')} />
              <span>
                <b>Mit eigenem Zertifikat</b>
                <small className="muted">Ihre Datei aus „Mein ELSTER“ und PIN.</small>
              </span>
            </label>
          </div>
        )}
        {weg === 'auftrag' ? (
          <>
            {auftrag.erteilt ? (
              <p className="muted ag-klein">
                Zugestimmt am {deDatum(auftrag.erteilt.am.slice(0, 10))} von {auftrag.erteilt.name}. Widerrufen geht in den Einstellungen.
              </p>
            ) : (
              <label className="ag-check ag-check-lang">
                <input type="checkbox" checked={zustimmung} onChange={(e) => setZustimmung(e.target.checked)} />
                <span>{auftrag.text}</span>
              </label>
            )}
            <label className="ag-check">
              <input type="checkbox" checked={geprueft} onChange={(e) => setGeprueft(e.target.checked)} />
              <span>Ich habe die Zahlen geprüft.</span>
            </label>
          </>
        ) : (
          <>
        <div className="field">
          <span>Ihre ELSTER-Zertifikatsdatei (.pfx)</span>
          <input ref={datei} type="file" accept=".pfx,.p12" hidden onChange={(e) => setZert(e.target.files?.[0] ?? null)} />
          <div className="ag-upload">
            <span className={zert ? undefined : 'muted'}>{zert ? zert.name : 'Aus „Mein ELSTER“, liegt auf Ihrem Rechner'}</span>
            <button type="button" className="btn small" onClick={() => datei.current?.click()}>
              Datei wählen
            </button>
          </div>
        </div>
        <label className="field">
          <span>PIN der Zertifikatsdatei</span>
          <input type="password" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
          <small>Datei und PIN nutzt Billtoro nur für dieses Senden und speichert sie nicht.</small>
        </label>
          </>
        )}
        {sonder && <SonderFeld wert={sv} onChange={setSv} />}
        {ok && <div className="notice ok">{ok}</div>}
        {error && <div className="notice bad">{error}</div>}
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={() => void los(true)} disabled={!!busy}>
            {busy === 'pruefen' ? 'Prüft …' : 'Erst prüfen'}
          </button>
          <button type="submit" className="btn primary" disabled={!!busy}>
            {busy === 'senden' ? 'Sendet …' : 'Jetzt senden'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
